import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { toast } from 'react-toastify';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Import service
import geospatialService from '../../services/geospatialService';

import MapLayers from './MapLayers';

const DRAWER_WIDTH = 450;

const GISPublicView = () => {
  const theme = useTheme();

  const { isAuthenticated, role } =  useSelector((state) => state.auth);
  
  // Map state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState([27.53866, 88.6174017]); // India center
  const [mapZoom, setMapZoom] = useState(5);
  
  // Data state
  const [geoData, setGeoData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    damageType: '',
    startDate: null,
    endDate: null,
    district: ''
  });
  
  // Layer visibility state
  const [layers, setLayers] = useState({
    markers: true,
    clusters: true,
    heatmap: true,
  });
  
  
  // Fetch data based on filters
  const fetchGeoData = async () => {
    setLoading(true);
    try {
      const params = {
        status: filters.status,
        damage_type: filters.damageType,
        start_date: filters.startDate,
        end_date: filters.endDate,
      };
      
      // Fetch cluster data
      const data = await geospatialService.getPublicClusterData(params);
      setGeoData(data);
      
      // Fetch heatmap data if heatmap layer is active
      if (layers.heatmap) {
        const heatData = await geospatialService.getPublicHeatmapData(params);
        setHeatmapData(heatData.points || []);
      }
    } catch (error) {
      console.error('Error fetching geospatial data:', error);
      toast.error('Failed to load geospatial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load
  useEffect(() => {
    fetchGeoData();
  }, [filters, layers.heatmap]);
  
  return (
    <Box
        component="main"
        sx={{
          left: 16,
          right: 16,
          bottom: 0,
          width: '100%',
          height: '70vh',
        }}
      >
        {/* Map Container */}
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          whenCreated={setMap}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Map Layers Component */}
          <MapLayers
            geoData={geoData}
            heatmapData={heatmapData}
            layers={layers}
          />
        </MapContainer>
        
        {/* Info Card - Bottom Left */}
        <Paper
          sx={{
            position: 'relative',
            bottom: 50,
            zIndex: 1000,
            p: 1.5,
            mx: 2,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: loading ? theme.palette.warning.main : theme.palette.success.main,
              }}
            />
            <Typography variant="body2" fontWeight="medium">
              {loading ? 'Loading...' : `${geoData?.features?.length || 0} Locations`}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
                | Click on markers to expand further details
            </Typography>
          </Box>
        </Paper>
    </Box>
  );
};

export default GISPublicView;
